import React, { useState, useEffect, createContext, useContext, useMemo } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [paywall, setPaywall] = useState({ open: false, message: "" });

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser(res.data))
        .catch(() => { localStorage.removeItem("token"); setToken(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, company_name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, company_name });
    localStorage.setItem("token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const refreshUser = async () => {
    if (!token) return null;
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const authAxios = useMemo(() => {
    const instance = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
    instance.interceptors.response.use(
      r => r,
      err => {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        // Subscription required (trial expired / canceled) → trigger global paywall
        if (status === 402 && detail && typeof detail === 'object' && detail.code === 'subscription_required') {
          setPaywall({ open: true, message: detail.message || "Start a 14-day free trial to send emails." });
          if (err.response) err.response.data = { ...err.response.data, detail: detail.message };
          return Promise.reject(err);
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, authAxios, refreshUser, paywall, setPaywall }}>
      {children}
    </AuthContext.Provider>
  );
};

export const LoadingScreen = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 bg-afa-navy rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse-soft">
        <Mail className="w-8 h-8 text-white" />
      </div>
      <p className="text-slate-500">Loading...</p>
    </div>
  </div>
);

export const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!token) return <Navigate to="/login" />;
  return children;
};
